import {CommonModule} from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {PaymentReceipt} from '@gatewee/types/payment';
import {preloadImageAsDataUrl, UtilsModule} from '@gatewee/utils';

@Component({
  selector: 'gatewee-payment-receipt-export-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, UtilsModule],
  templateUrl: './gatewee-payment-receipt-export-card.component.html',
  styleUrls: ['./gatewee-payment-receipt-export-card.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class GateweePaymentReceiptExportCardComponent implements OnChanges {
  @Input({required: true}) receipt!: PaymentReceipt;
  @Input() amount: number | null = null;
  @Input() requestedAmount: number | null = null;
  @Input() calculatedFee: number | null = null;
  @Input() finalAmount: number | null = null;
  @Input() recipientDisplayName = '';
  @Input() bankDisplayLine = '';
  @Input() providerLabel = '';
  @Input() agencyDisplay = '';
  @Input() accountDisplay = '';
  @Input() digitableLine = '';
  @Input() isBankSlip = false;
  @Input() logoUrl: string | null = null;
  @Input() brandPrimaryColor = '#ff5c00';
  @Input() domainTitle = '';

  @ViewChild('exportRoot', {read: ElementRef})
  exportRoot?: ElementRef<HTMLElement>;

  /** Logo src for capture (data URL preferred; remote URL when CORS allows). */
  displayLogoUrl: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['logoUrl']) {
      void this.resolveLogo();
    }
  }

  get brandAccentStyle(): Record<string, string> {
    return {borderTopColor: this.brandPrimaryColor || '#ff5c00'};
  }

  get resolvedRequestedAmount(): number | null {
    return this.requestedAmount ?? this.amount;
  }

  get resolvedFinalAmount(): number | null {
    if (this.finalAmount !== null && this.finalAmount !== undefined) {
      return this.finalAmount;
    }
    if (this.resolvedRequestedAmount === null) {
      return null;
    }
    return Math.max(
      0,
      this.resolvedRequestedAmount - (this.calculatedFee ?? 0),
    );
  }

  get hasFee(): boolean {
    return (this.calculatedFee ?? 0) > 0;
  }

  /** Await logo preload, fonts, and layout before html-to-image capture. */
  async prepareForCapture(): Promise<void> {
    await this.resolveLogo();
    await this.ensureInterFontsLoaded();
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  private async ensureInterFontsLoaded(): Promise<void> {
    const loads = [
      document.fonts.load('400 11px Inter'),
      document.fonts.load('400 13px Inter'),
      document.fonts.load('400 14px Inter'),
      document.fonts.load('600 14px Inter'),
      document.fonts.load('800 13px Inter'),
      document.fonts.load('800 14px Inter'),
      document.fonts.load('800 16px Inter'),
      document.fonts.load('800 18px Inter'),
    ];
    await Promise.allSettled(loads);
    await document.fonts.ready;
  }

  private async resolveLogo(): Promise<void> {
    const raw = this.logoUrl?.trim();
    if (!raw) {
      this.displayLogoUrl = null;
      return;
    }
    const dataUrl = await preloadImageAsDataUrl(raw);
    this.displayLogoUrl = dataUrl ?? raw;
  }
}
